import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function BybitPanel({ onClose }) {
  const [pair, setPair] = useState('BTCUSDT');
  const [isEditingPair, setIsEditingPair] = useState(false);
  const pairInputRef = useRef(null);
  const [pairDraft, setPairDraft] = useState('BTCUSDT');

  const [baseQty1, setBaseQty1] = useState('0');

  const [orderSubmitting, setOrderSubmitting] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [orderOk, setOrderOk] = useState(false);

  const normalizedPair = useMemo(() => String(pair ?? '').trim().toUpperCase(), [pair]);

  const [price, setPrice] = useState('2137');
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState(null);

  const [klines, setKlines] = useState([]);
  const [klinesLoading, setKlinesLoading] = useState(false);
  const [klinesError, setKlinesError] = useState(null);

  const fetchPrice = async (symbol) => {
    const s = String(symbol ?? '').trim().toUpperCase();
    if (!s) return;

    setPriceLoading(true);
    setPriceError(null);
    try { 
      const res = await fetch(`https://api.bybit.com/v5/market/tickers?category=spot&symbol=${encodeURIComponent(s)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const lastPrice = data?.result?.list?.[0]?.lastPrice;
      if (lastPrice == null) throw new Error('No price in response');
      setPrice(String(lastPrice));
    } catch (e) {
      setPriceError(e?.message || 'Failed to fetch price');
    } finally {
      setPriceLoading(false);
    }
  };

  const fetchKlines = async (symbol) => {
    const s = String(symbol ?? '').trim().toUpperCase();
    if (!s) return;

    setKlinesLoading(true);
    setKlinesError(null);
    try {
      const res = await fetch(`https://api.bybit.com/v5/market/kline?category=spot&symbol=${encodeURIComponent(s)}&interval=5`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = data?.result?.list;
      if (!Array.isArray(list) || list.length === 0) throw new Error('No klines in response');

      // Bybit format (typical): [startTime, open, high, low, close, volume, turnover]
      // Order is often newest->oldest; normalize to oldest->newest.
      const parsed = list
        .map((row) => {
          const t = Number(row?.[0]);
          const o = Number(row?.[1]);
          const h = Number(row?.[2]);
          const l = Number(row?.[3]);
          const c = Number(row?.[4]);
          return { t, o, h, l, c };
        })
        .filter((k) => Number.isFinite(k.t) && Number.isFinite(k.o) && Number.isFinite(k.h) && Number.isFinite(k.l) && Number.isFinite(k.c))
        .sort((a, b) => a.t - b.t);

      setKlines(parsed);
    } catch (e) {
      setKlinesError(e?.message || 'Failed to fetch klines');
      setKlines([]);
    } finally {
      setKlinesLoading(false);
    }
  };

  // Poll price every 5 seconds while the panel is displayed (mounted).
  // Pause polling while user is editing pair.
  useEffect(() => {
    if (isEditingPair) return;

    // Fetch immediately on mount / symbol change
    fetchPrice(normalizedPair);
    fetchKlines(normalizedPair);
    const id = setInterval(() => fetchPrice(normalizedPair), 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedPair, isEditingPair]);

  // Autofocus when switching to edit mode.
  useEffect(() => {
    if (!isEditingPair) return;
    // next tick so the input exists
    const t = setTimeout(() => {
      pairInputRef.current?.focus?.();
      pairInputRef.current?.select?.();
    }, 0);
    return () => clearTimeout(t);
  }, [isEditingPair]);

  const openPairDialog = () => {
    setPairDraft(normalizedPair || '');
    setIsEditingPair(true);
  };

  const commitPairEditing = () => {
    const next = String(pairDraft ?? '').trim().toUpperCase();
    if (next) setPair(next);
    setIsEditingPair(false);
  };

  const cancelPairEditing = () => {
    setIsEditingPair(false);
  };

  const submitMarketOrder = async (side) => {
    const s = String(side || '').toUpperCase();
    if (s !== 'BUY' && s !== 'SELL') return;

    const qty = Number(baseQty1);
    if (!Number.isFinite(qty) || qty <= 0) {
      setOrderOk(false);
      setOrderError('Quote qty must be a positive number');
      return;
    }

    setOrderSubmitting(true);
    setOrderError(null);
    setOrderOk(false);
    try {
      const payload = {
        exchange: 'BYBIT',
        orderType: 'MARKET',
        baseAsset: normalizedPair,
        quoteAsset: '',
        baseAssetQuantity: qty,
        validFor: 5000,
        executed: false,
        side: s,
      };

      const res = await fetch('https://binanas.zenit.poznan.pl/api/v1/market-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'chujwdupepolicji'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
      }

      setOrderOk(true);
    } catch (e) {
      setOrderOk(false);
      setOrderError(e?.message || 'Order failed');
    } finally {
      setOrderSubmitting(false);
    }
  };

  const chart = useMemo(() => {
    const data = klines;
    if (!Array.isArray(data) || data.length === 0) return null;

    const width = 600;
    const height = 260;
    const padX = 8;
    const padY = 10;

    const highs = data.map(d => d.h);
    const lows = data.map(d => d.l);
    const maxY = Math.max(...highs);
    const minY = Math.min(...lows);
    const range = maxY - minY || 1;

    const y = (v) => {
      const t = (v - minY) / range;
      // invert y-axis; keep padding
      return (height - padY) - t * (height - padY * 2);
    };

    const n = data.length; 
    const innerW = width - padX * 2;
    const step = innerW / n;
    const bodyW = Math.max(1, Math.min(8, step * 0.65));

    return (
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        aria-label="Kline chart"
        style={{ display: 'block' }}
      >
        <rect x={0} y={0} width={width} height={height} fill="#000" />
        {/* background grid line */}
        <line x1={0} y1={height - 0.5} x2={width} y2={height - 0.5} stroke="#888" />

        {data.map((d, i) => {
          const xMid = padX + i * step + step / 2;
          const up = d.c >= d.o;
          const color = up ? '#0a0' : '#c00';

          const yOpen = y(d.o);
          const yClose = y(d.c);
          const yHigh = y(d.h);
          const yLow = y(d.l);

          const bodyTop = Math.min(yOpen, yClose);
          const bodyBot = Math.max(yOpen, yClose);
          const bodyH = Math.max(1, bodyBot - bodyTop);

          return (
            <g key={d.t ?? i}>
              {/* wick */}
              <line x1={xMid} y1={yHigh} x2={xMid} y2={yLow} stroke={color} strokeWidth={1} />
              {/* body */}
              <rect
                x={xMid - bodyW / 2}
                y={bodyTop}
                width={bodyW}
                height={bodyH}
                fill={up ? 'rgba(0,170,0,0.35)' : 'rgba(200,0,0,0.35)'}
                stroke={color}
                strokeWidth={1}
                rx={1}
              />
            </g>
          );
        })}
      </svg>
    );
  }, [klines]);

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: '24px 12px 18px 12px',
      boxShadow: '0 2px 18px rgba(0,0,0,0.22)',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      minWidth: 0,
      width: '520px',
      maxWidth: '98vw',
      minHeight: 0,
      height: '650px',
      maxHeight: '96vh',
      overflow: 'auto',
      zIndex: 10000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 16, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Close"
      >
        ✖️
      </button>

      <h2 style={{ marginBottom: 16 }}>Bybit</h2>

      {/* content */}
      <div style={{
        flex: 1,
        borderRadius: 12,
        border: '1px solid #eee',
        padding: 16,
        background: '#f8f8f8',
        color: '#222',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        minHeight: 0
      }}>
        {/* top: price + pair (compact) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 10,
            fontSize: '1.05rem',
            fontWeight: 700
          }}>
            <span style={{ opacity: 0.85 }}>Price</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {priceLoading ? '…' : price}
            </span>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 700
          }}>
            <span style={{ opacity: 0.85 }}>Pair</span>
            <div
              role="button"
              tabIndex={0}
              onClick={openPairDialog}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openPairDialog();
              }}
              title="Click to change"
              style={{
                padding: '6px 10px',
                borderRadius: 10,
                border: '1px solid #ddd',
                fontSize: '1.02rem',
                background: '#fff',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              {normalizedPair || '—'}
            </div>
          </div>
        </div>

        {priceError && (
          <div style={{
            color: '#c00',
            background: 'rgba(255,0,0,0.06)',
            border: '1px solid rgba(255,0,0,0.18)',
            padding: '8px 10px',
            borderRadius: 10,
            fontWeight: 600
          }}>
            Price error: {priceError}
          </div>
        )}

        {(orderError || orderOk) && (
          <div style={{
            borderRadius: 10,
            padding: '8px 10px',
            fontWeight: 700,
            border: orderOk ? '1px solid rgba(0,170,0,0.25)' : '1px solid rgba(255,0,0,0.18)',
            background: orderOk ? 'rgba(0,170,0,0.08)' : 'rgba(255,0,0,0.06)',
            color: orderOk ? '#070' : '#c00'
          }}>
            {orderOk ? 'Order submitted' : `Order error: ${orderError}`}
          </div>
        )}

        {/* middle: chart placeholder */}
        <div style={{
          flex: 1,
          minHeight: 0,
          borderRadius: 12,
          border: '1px dashed #cfcfcf',
          background: '#eee',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ddd',
          fontWeight: 700,
          letterSpacing: 0.2
        }}>
          {klinesLoading ? (
            'Loading chart…'
          ) : klinesError ? (
            <div style={{
              color: '#c00',
              padding: 12,
              textAlign: 'center',
              fontWeight: 700
            }}>
              Chart error: {klinesError}
            </div>
          ) : chart ? (
            <div style={{ width: '100%', height: '100%' }}>
              {chart}
            </div>
          ) : (
            'No chart data'
          )}
        </div>

        {/* bottom: qty */}
        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontWeight: 700 }}>
          <span>Quote qty</span>
          <input
            value={baseQty1}
            onChange={(e) => setBaseQty1(e.target.value)}
            style={{
              width: 180,
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid #ddd',
              fontSize: '1.05rem'
            }}
            inputMode="decimal"
            autoComplete="off"
          />
        </label>
      </div>

      {/* bottom half: buy/sell */}
      <div style={{
        marginTop: 16,
        display: 'flex',
        gap: 12
      }}>
        <button
          type="button"
          style={{
            flex: 1,
            padding: '12px 0',
            fontSize: '1.2rem',
            fontWeight: 700,
            borderRadius: 10,
            border: '1px solid rgba(0,160,0,0.35)',
            background: 'rgba(0,255,0,0.08)',
            color: '#0a0',
            cursor: 'pointer'
          }}
          onClick={() => submitMarketOrder('BUY')}
          disabled={orderSubmitting || isEditingPair}
        >
          {orderSubmitting ? '…' : 'Buy'}
        </button>

        <button
          type="button"
          style={{
            flex: 1,
            padding: '12px 0',
            fontSize: '1.2rem',
            fontWeight: 700,
            borderRadius: 10,
            border: '1px solid rgba(200,0,0,0.35)',
            background: 'rgba(255,0,0,0.08)',
            color: '#c00',
            cursor: 'pointer'
          }}
          onClick={() => submitMarketOrder('SELL')}
          disabled={orderSubmitting || isEditingPair}
        >
          {orderSubmitting ? '…' : 'Sell'}
        </button>
      </div>

      {isEditingPair && (
        <div
          role="dialog"
          aria-label="Edit pair"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            borderRadius: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cancelPairEditing();
          }}
        >
          <div style={{
            width: '420px',
            maxWidth: '95vw',
            background: '#fff',
            borderRadius: 14,
            boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
            border: '1px solid rgba(0,0,0,0.08)',
            padding: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ fontSize: '1.15rem', fontWeight: 800 }}>Change pair</div>
              <button
                type="button"
                onClick={cancelPairEditing}
                aria-label="Close"
                style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}
              >
                ✖️
              </button>
            </div>

            <div style={{ marginTop: 12 }}>
              <input
                ref={pairInputRef}
                value={pairDraft}
                onChange={(e) => setPairDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitPairEditing();
                  if (e.key === 'Escape') cancelPairEditing();
                }}
                style={{
                  width: '100%',
                  padding: '12px 12px',
                  borderRadius: 10,
                  border: '1px solid #ddd',
                  fontSize: '1.05rem',
                  fontWeight: 700
                }}
                placeholder="BTCUSDT"
                inputMode="text"
                autoComplete="off"
              />
              <div style={{ marginTop: 8, color: '#666', fontSize: '0.95rem' }}>
                Example: BTCUSDT, ETHUSDT
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button
                type="button"
                onClick={cancelPairEditing}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 10,
                  border: '1px solid #ddd',
                  background: '#f6f6f6',
                  fontWeight: 800,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={commitPairEditing}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: 10,
                  border: '1px solid rgba(0,153,255,0.35)',
                  background: 'rgba(0,153,255,0.10)',
                  color: '#06c',
                  fontWeight: 900,
                  cursor: 'pointer'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
